import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ROUTES } from '../../constants/routes';
import { UI, EASE, h2, lead, btn, lnk, SECTION_GAP } from '../../styles/tl5';
import { useListFaqsQuery, useGetContactInfoQuery } from '../../store/siteContentApi';

// "Questions?" — a prompt beside an accordion.
//
// Native <details> rather than MUI Accordion: the reference's open/close
// is a state change on the element itself, and the + turning into a −
// comes from two bars under [open]. No JS state to keep in sync, and it
// still works with the keyboard and with JS disabled.
//
// One addition to the reference: it writes "Message us on WhatsApp" but
// only links the Help centre, leaving the invitation with nowhere to go.
// The existing WhatsApp button is kept so the sentence is true.
// The admin's FAQ list runs long — the Help centre is the place for all
// of it. The reference prints five here, enough to answer the common
// questions without the column outrunning the prompt beside it.
const HOME_FAQ_COUNT = 5;

const HomeFaq = () => {
  const { data: faqs = [] } = useListFaqsQuery();
  const { data: contact } = useGetContactInfoQuery();
  if (!faqs.length) return null;

  const wa = `https://wa.me/${String(contact?.whatsapp || '').replace(/\D/g, '')}`;

  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        maxWidth: 1320,
        mx: 'auto',
        pt: SECTION_GAP,
        px: 2,
        pb: 5,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
        gap: 3,
        alignItems: 'start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          px: 1,
          position: { md: 'sticky' },
          top: { md: 104 },
        }}
      >
        <Typography component="h2" sx={h2}>Questions?</Typography>
        <Typography sx={{ ...lead, fontSize: 16 }}>
          Message us on WhatsApp. A real person replies.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, mt: 0.75 }}>
          <Box
            component="a"
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ ...btn, background: 'var(--tl-wa)' }}
          >
            Chat on WhatsApp
          </Box>
          <Box component={Link} to={ROUTES.HELP} sx={lnk}>Help centre →</Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {faqs.slice(0, HOME_FAQ_COUNT).map((f) => (
          <Box
            component="details"
            key={f._id || f.question}
            sx={{
              px: '22px',
              borderRadius: '22px',
              bgcolor: '#FFFFFF',
              transition: 'box-shadow .3s',
              '&[open]': { boxShadow: '0 12px 30px -16px rgba(11,43,39,0.35)' },
              // The vertical bar of the + rotates flat, leaving a −.
              '&[open] .tl-pm::after': { transform: 'rotate(0)' },
              '& summary::-webkit-details-marker': { display: 'none' },
            }}
          >
            <Box
              component="summary"
              sx={{
                listStyle: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 2.5,
                cursor: 'pointer',
                fontFamily: UI,
                fontSize: 17,
                fontWeight: 800,
                color: 'var(--tl-g9)',
              }}
            >
              {f.question}
              <Box
                className="tl-pm"
                sx={{
                  position: 'relative',
                  flex: 'none',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'var(--tl-mint)',
                  '&::before, &::after': {
                    content: '""',
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: 12,
                    height: 2,
                    m: '-1px 0 0 -6px',
                    borderRadius: '2px',
                    bgcolor: 'var(--tl-g9)',
                    transition: `transform .45s ${EASE}`,
                  },
                  '&::after': { transform: 'rotate(90deg)' },
                }}
              />
            </Box>
            <Typography
              sx={{
                m: '0 0 20px',
                fontFamily: UI,
                fontSize: 15,
                lineHeight: 1.65,
                color: 'var(--tl-muted)',
              }}
            >
              {f.answer}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default HomeFaq;
